import { Modal, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import firebase from "firebase/compat/app";
import { shallowEqual, useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";

const Dragger = Upload.Dragger;

const ALLOWED_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "image/tiff": [".tif", ".tiff"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/msword": [".doc"],
  "application/vnd.oasis.opendocument.text": [".odt"],
  "application/rtf": [".rtf"],
  "text/plain": [".txt"],
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
};

const ALLOWED_EXTENSIONS = Object.values(ALLOWED_TYPES).flat();

function validateFile(file) {
  const ext = "." + file.name.split(".").pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext) && !ALLOWED_TYPES[file.type]) {
    return `Type de fichier non autorisé : ${ext}`;
  }
  return null;
}

const ImageUploadModal = ({ visible, onCancel, onOk }) => {
  const user = useSelector((state) => state.user, shallowEqual);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(0);

  const canConfirm = useMemo(() => {
    return files.length > 0 && uploading === 0;
  }, [files, uploading]);

  useEffect(() => {
    setFiles([]);
  }, [visible]);

  const beforeUpload = (file) => {
    const error = validateFile(file);
    if (error) {
      message.error(error);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const onFileUpload = async (file) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const name = `${Date.now()}-${safeName}`;
    const storageRef = firebase.storage().ref("attachments/" + user.uid + "/" + name);
    await storageRef.put(file);
    const url = await storageRef.getDownloadURL();
    if (file.type === "application/pdf") {
      const reader = new FileReader();

      reader.readAsBinaryString(file);
      reader.onloadend = () => {
        let count = 1;
        try {
          count = reader.result.match(/\/Type[\s]*\/Page[^s]/g).length;
        } catch {
          // do nothing
        }

        setFiles((f) => [
          ...f,
          {
            filename: file.name,
            originalFile: {
              name: file.name,
              size: file.size,
              type: file.type,
              pages: count,
            },
            mimetype: file.type,
            pages: count,
            size: file.size,
            url,
          },
        ]);
      };
    } else {
      setFiles((f) => [
        ...f,
        {
          filename: file.name,
          originalFile: {
            name: file.name,
            size: file.size,
            type: file.type,
            pages: 1,
          },
          mimetype: file.type,
          size: file.size,
          url,
          pages: 1,
        },
      ]);
    }
  };

  return (
    <Modal
      title="Ajouter des documents"
      visible={visible}
      onOk={(e) => {
        onOk(files);
      }}
      onCancel={onCancel}
      width="90%"
      okButtonProps={{
        disabled: !canConfirm,
      }}
    >
      <Dragger
        multiple={true}
        beforeUpload={beforeUpload}
        customRequest={({ file, onSuccess }) => {
          onFileUpload(file).then(() => onSuccess());
        }}
        listType="picture"
        accept={ALLOWED_EXTENSIONS.join(",")}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Cliquez ou glissez vos fichiers ici</p>
        <p className="ant-upload-hint">
          Formats acceptés : PDF, images, Word, texte, Excel.
        </p>
      </Dragger>
    </Modal>
  );
};

export default ImageUploadModal;
